import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  Label,
  SkeletonSpinnerCentered,
} from '@civilgrid/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  archiveExportEscalation,
  type ExportEscalation,
  type ExportEscalationDetail,
  getExportEscalation,
  runExportEscalation,
  updateExportEscalationNotes,
} from '~/api/internal';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';
import { EXPORT_ESCALATION_POLL_INTERVAL_MS, EXPORT_ESCALATION_REASON_CONFIG } from './constants';

export type ExportEscalationDetailDialogProps = {
  escalationId: string | null;
  reason: ExportEscalation['escalationReason'];
  open: boolean;
  onClose: () => void;
  onRunTriggered?: (id: string) => void;
};

function formatTimestamp(value: string | null) {
  return value ? DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED) : '-';
}

function formatUser(userValue: { name: string | null; email: string } | null) {
  if (!userValue) return '-';
  return userValue.name ?? userValue.email;
}

export function ExportEscalationDetailDialog({
  escalationId,
  reason,
  open,
  onClose,
  onRunTriggered,
}: ExportEscalationDetailDialogProps) {
  const queryClient = useQueryClient();
  const {
    data,
    isLoading,
    isError,
    refetch: refetchEscalation,
  } = useQuery({
    queryKey: ['export-escalation', escalationId],
    queryFn: () => getExportEscalation(escalationId as string),
    enabled: open && !!escalationId,
    // Mirrors the table's own polling — this dialog can be reopened on a row that's still running.
    refetchInterval: (q) => (q.state.data?.isRunning ? EXPORT_ESCALATION_POLL_INTERVAL_MS : false),
  });

  // recordEscalationOutcome can flip a Restricted escalation to Failed mid-run, after the tab's
  // static `reason` prop was already chosen — the live value from the query is the one to trust.
  const displayReason = data?.escalationReason ?? reason;

  const [notes, setNotes] = useState('');
  useEffect(() => {
    setNotes(data?.notes ?? '');
  }, [data?.notes]);

  const notesMutation = useMutation({
    mutationFn: () => updateExportEscalationNotes(escalationId as string, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-escalation', escalationId] });
      capturePosthogEvent(POSTHOG_EVENTS.exportEscalationNotesSaved, {
        escalation_id: escalationId as string,
        escalation_reason: displayReason,
      });
      Notify.info({ message: 'Notes saved' });
    },
    onError: () => {
      Notify.error({ message: 'Failed to save notes. Please try again.' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveExportEscalation(escalationId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-escalation', escalationId] });
      queryClient.invalidateQueries({ queryKey: ['admin-export-escalations'] });
      capturePosthogEvent(POSTHOG_EVENTS.exportEscalationArchived, {
        escalation_id: escalationId as string,
        previous_status: data?.escalationStatus ?? 'unknown',
        escalation_reason: displayReason,
      });
      Notify.info({ message: 'Request archived' });
      onClose();
    },
    onError: () => {
      Notify.error({ message: 'Failed to archive. Please try again.' });
    },
  });

  const runMutation = useMutation({
    mutationFn: () => runExportEscalation(escalationId as string),
    // No invalidate in onSuccess: the server can still lag this optimistic isRunning until the job's
    // own triggeredAt write lands — onRunTriggered covers that gap instead.
    onMutate: () => {
      queryClient.setQueryData(['export-escalation', escalationId], (old?: ExportEscalationDetail) =>
        old ? { ...old, isRunning: true } : old,
      );
      queryClient.setQueriesData(
        { queryKey: ['admin-export-escalations'] },
        (old?: { items: ExportEscalation[]; count: number }) =>
          old
            ? {
                ...old,
                items: old.items.map((item) => (item.id === escalationId ? { ...item, isRunning: true } : item)),
              }
            : old,
      );
    },
    onSuccess: () => {
      capturePosthogEvent(POSTHOG_EVENTS.exportEscalationRunTriggered, {
        escalation_id: escalationId as string,
        escalation_reason: displayReason,
      });
      onRunTriggered?.(escalationId as string);
      Notify.info({ message: 'Export triggered' });
      onClose();
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['export-escalation', escalationId] });
      queryClient.invalidateQueries({ queryKey: ['admin-export-escalations'] });
      Notify.error({ message: 'Failed to trigger export. Please try again.' });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="w-150"
        data-testid="export-escalation-detail-dialog"
        title={EXPORT_ESCALATION_REASON_CONFIG[displayReason].dialogTitle}
      >
        <DialogBody className="flex flex-col gap-3 w-full max-h-[70vh] overflow-y-auto">
          {isLoading && (
            <div className="flex min-h-40 items-center justify-center">
              <SkeletonSpinnerCentered size={24} />
            </div>
          )}
          {!isLoading && isError && (
            <div className="flex flex-col items-start gap-2" data-testid="export-escalation-detail-error">
              <p className="text-sm text-red-60">Failed to load this escalation.</p>
              <Button
                variant="secondary"
                size="small"
                onClick={() => refetchEscalation()}
                data-testid="export-escalation-detail-retry"
              >
                Retry
              </Button>
            </div>
          )}
          {data && (
            <>
              <div className="flex items-center justify-between">
                <span className="capitalize text-sm font-medium">
                  {data.isRunning ? 'Running…' : data.escalationStatus}
                </span>
                <span className="text-xs text-grey-60">Requested {formatTimestamp(data.requestedAt)}</span>
              </div>

              <div>
                <Label>Requestor</Label>
                <div className="text-sm">
                  {data.requestor.name ?? data.requestor.email} ({data.requestor.email})
                </div>
              </div>

              <div>
                <Label>Project</Label>
                <div className="text-sm">
                  <a
                    href={`${window.location.origin}/projects/${data.project.id}/downloads`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-dark underline"
                    data-testid="export-escalation-detail-project-link"
                  >
                    {data.project.name}
                  </a>
                </div>
              </div>

              <div>
                <Label>Organization</Label>
                <div className="text-sm">{data.organization.name}</div>
              </div>

              <div>
                <Label>Geometry</Label>
                <div className="text-sm capitalize">
                  {data.project.geometryType}, {data.project.segmentCount} segment
                  {data.project.segmentCount === 1 ? '' : 's'}
                </div>
              </div>

              <div>
                <Label>Area</Label>
                <div className="text-sm">{data.area.toFixed(2)} acres</div>
              </div>

              <div>
                <Label>File Formats</Label>
                <div className="text-sm">{data.formats.join(', ')}</div>
              </div>

              <div>
                <Label>Projection</Label>
                <div className="text-sm">{data.projection?.title ?? '-'}</div>
              </div>

              <div>
                <Label>Requested Layers</Label>
                <ul className="text-sm list-disc list-inside">
                  {data.layers.map((layer) => (
                    <li key={layer}>{layer}</li>
                  ))}
                </ul>
              </div>

              <div>
                <Label>Extract Template</Label>
                <div className="text-sm">{data.extractTemplateName}</div>
              </div>

              <div>
                <a
                  href={data.geojsonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-dark underline text-sm"
                  data-testid="export-escalation-detail-geojson-link"
                >
                  View Project Geometry
                </a>
              </div>

              <div>
                <Label>Notes</Label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Add notes..."
                  className="w-full rounded-md border border-grey-30 bg-transparent px-3 py-2 text-sm resize-none focus:outline-none focus:border-focus"
                  data-testid="export-escalation-detail-notes-input"
                />
                <div className="flex justify-end mt-1">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => notesMutation.mutate()}
                    disabled={notesMutation.isPending || notes === (data.notes ?? '')}
                    data-testid="export-escalation-detail-notes-save"
                  >
                    {notesMutation.isPending ? 'Saving…' : 'Save Notes'}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-1 text-xs text-grey-60">
                <span>
                  Triggered by {formatUser(data.triggeredBy)} at {formatTimestamp(data.triggeredAt)}
                </span>
                <span>
                  Archived by {formatUser(data.archivedBy)} at {formatTimestamp(data.archivedAt)}
                </span>
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          {data && (
            <Button
              variant="secondary"
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending || data.escalationStatus !== 'pending' || data.isRunning}
              data-testid="export-escalation-detail-run"
            >
              {runMutation.isPending ? 'Triggering…' : EXPORT_ESCALATION_REASON_CONFIG[displayReason].runButtonLabel}
            </Button>
          )}
          {data && (
            <Button
              variant="secondary"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending || data.escalationStatus === 'archived' || data.isRunning}
              data-testid="export-escalation-detail-archive"
            >
              {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} data-testid="export-escalation-detail-dialog-done">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
